import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Grid,
  Card,
  Chip,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { getAdvertisingBanners } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";

const PromotionsSectionCompo = () => {
  const { i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAdvertisingBanners();
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        // only active banners, sorted by display_order
        const active = list
          .filter((b) => b.is_active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setBanners(active);
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getTitle = (b) =>
    isAr ? b.title_ar || b.title_en || "" : b.title_en || b.title_ar || "";

  const getDescription = (b) =>
    isAr
      ? b.description_ar || b.description_en || ""
      : b.description_en || b.description_ar || "";

  const formatDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString(lang, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return d;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 120,
        }}
      >
        <CircularProgress size={36} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (error || !banners.length) return null;

  // single banner → full width hero-style
  if (banners.length === 1) {
    const b = banners[0];
    const img = getImageUrl(b.image_web || b.image);
    const title = getTitle(b);
    const desc = getDescription(b);

    return (
      <Box sx={{ py: { xs: 3, md: 5 } }}>
        <Container maxWidth="xl">
          <Card
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "grey.100",
              "&:hover .banner-img": { transform: "scale(1.03)" },
            }}
          >
            <Box
              className="banner-img"
              component="img"
              src={img}
              alt={title}
              sx={{
                width: "100%",
                height: { xs: 220, sm: 320, md: 420 },
                objectFit: "cover",
                display: "block",
                transition: "transform 0.6s ease",
              }}
            />
            {(title || desc) && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)",
                  p: { xs: 2, md: 3.5 },
                }}
              >
                {title && (
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      color: "#fff",
                      mb: 0.5,
                      fontSize: { xs: "1.1rem", md: "1.5rem" },
                      textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {desc && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: { xs: "0.8rem", md: "0.9rem" },
                    }}
                  >
                    {desc}
                  </Typography>
                )}
              </Box>
            )}
          </Card>
        </Container>
      </Box>
    );
  }

  // multiple banners → responsive grid
  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        position: "relative",
        bgcolor: "background.paper",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background:
            "linear-gradient(90deg, transparent 0%, #d4af37 50%, transparent 100%)",
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              background:
                "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)",
              borderRadius: "50%",
              p: 1.2,
              display: "flex",
              boxShadow: "0 4px 16px rgba(212, 175, 55, 0.35)",
            }}
          >
            <LocalOfferIcon
              sx={{ fontSize: { xs: 22, md: 28 }, color: "#fff" }}
            />
          </Box>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2rem" },
              background:
                "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
            }}
          >
            {isAr ? "العروض " : "Offers "}
          </Typography>
        </Box>

        {/* Banners Grid */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {banners.map((b, idx) => {
            const img = getImageUrl(b.image_web || b.image);
            const title = getTitle(b);
            const desc = getDescription(b);
            // first banner takes full width, rest are split
            const isFirst = idx === 0;
            const dateRange =
              b.start_date || b.end_date
                ? `${formatDate(b.start_date)} – ${formatDate(b.end_date)}`
                : "";

            return (
              <Grid
                item
                xs={12}
                sm={isFirst ? 12 : 6}
                md={isFirst ? 12 : banners.length <= 3 ? 6 : 4}
                key={b.id}
              >
                <Card
                  elevation={0}
                  sx={{
                    position: "relative",
                    borderRadius: 4,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "grey.100",
                    height: "100%",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "default",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 40px rgba(212, 175, 55, 0.18)",
                      borderColor: "primary.light",
                      "& .banner-img": { transform: "scale(1.06)" },
                      "& .banner-overlay": { opacity: 1 },
                    },
                  }}
                >
                  {/* Image */}
                  <Box sx={{ overflow: "hidden" }}>
                    <Box
                      className="banner-img"
                      component="img"
                      src={img}
                      alt={title}
                      sx={{
                        width: "100%",
                        height: {
                          xs: isFirst ? 220 : 180,
                          sm: isFirst ? 320 : 220,
                          md: isFirst ? 380 : 260,
                        },
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  </Box>

                  {/* Gradient Overlay with text */}
                  <Box
                    className="banner-overlay"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background:
                        "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.75) 100%)",
                      p: { xs: 2, md: 3 },
                      opacity: title || desc ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    {title && (
                      <Typography
                        variant={isFirst ? "h5" : "subtitle1"}
                        fontWeight={800}
                        sx={{
                          color: "#fff",
                          mb: desc ? 0.5 : 0,
                          fontSize: {
                            xs: isFirst ? "1.1rem" : "0.95rem",
                            md: isFirst ? "1.5rem" : "1.1rem",
                          },
                          textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {title}
                      </Typography>
                    )}

                    {desc && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.85)",
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          mb: dateRange ? 1 : 0,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.5,
                        }}
                      >
                        {desc}
                      </Typography>
                    )}

                    {dateRange && (
                      <Chip
                        icon={
                          <CalendarTodayIcon
                            sx={{ fontSize: 14, color: "#fff !important" }}
                          />
                        }
                        label={dateRange}
                        size="small"
                        sx={{
                          bgcolor: "rgba(213, 170, 47, 0.85)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          backdropFilter: "blur(8px)",
                          height: 26,
                          "& .MuiChip-icon": { color: "#fff" },
                        }}
                      />
                    )}
                  </Box>

                  {/* Badge for order */}
                  {isFirst && banners.length > 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: isAr ? "auto" : 16,
                        right: isAr ? 16 : "auto",
                        bgcolor: "rgba(213, 170, 47, 0.9)",
                        backdropFilter: "blur(12px)",
                        color: "#fff",
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 800,
                        fontSize: "0.75rem",
                        boxShadow: "0 4px 16px rgba(213, 170, 47, 0.4)",
                      }}
                    >
                      {isAr ? "⭐ عرض مميز" : "⭐ Featured"}
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default PromotionsSectionCompo;
